import React, { useCallback } from 'react'

import Icon from '../../../Icon2/Icon'
import Help from '../../../Help/Help'
import { Truncate } from '../../../Truncate/Truncate'
import type { UrlPatternSuggestionRowIcon } from './utils/urlManagementV2Utils'

export type UrlPatternMenuRowProps = {
  /** Applied to the row button (`data-test-id`). */
  testId: string
  /** Leading icon (Figma). Pass `null` for rows without an icon (e.g. “custom text”). */
  rowIcon: UrlPatternSuggestionRowIcon | null
  /** Primary label (left), e.g. preset name or token label. */
  title: string
  /** Optional class on the primary title wrapper (e.g. quoted custom-fragment styling). */
  titleClassName?: string
  /** Secondary line (right), e.g. description or pattern path — always plain text from callers. */
  subtitle?: string
  /** Optional help tooltip rendered next to the title (used by full preset rows). */
  titleHelpText?: string
  /** Shows chevron for drill-down rows (field / taxonomy parent). */
  showChevron?: boolean
  onClick: () => void
  onMouseDown?: (e: React.MouseEvent) => void
}

/**
 * Single suggestion row in URL pattern dropdowns (syntax presets, tokens, field/taxonomy parents, submenus).
 */
const UrlPatternMenuRow = ({
  testId,
  rowIcon,
  title,
  titleClassName,
  subtitle,
  titleHelpText,
  showChevron = false,
  onClick,
  onMouseDown = (e: React.MouseEvent) => e.preventDefault()
}: UrlPatternMenuRowProps) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    },
    [onClick]
  )

  const hasSubtitle = subtitle != null && subtitle !== ''
  const secondary = (
    <span className="UrlPatternTextDropdown__menuRowSecondary">
      {hasSubtitle && (
        <span className="UrlPatternTextDropdown__suggestionMeta">
          <Truncate
            testId={`${testId}-secondaryText`}
            truncateFrom="end"
            isResponsive
            tooltip={{ position: 'bottom' }}
          >
            {subtitle}
          </Truncate>
        </span>
      )}
      {/* {showChevron && (
        <Icon
          icon="ArrowLineRight"
          version="v2"
          size="small"
          testId="cs-ct-field-url-v2-menu-row-chevron"
        />
      )} */}
    </span>
  )

  return (
    <div
      className={`UrlPatternTextDropdown__menuRowButton${rowIcon == null ? ' UrlPatternTextDropdown__menuRowButton--noLeadingIcon' : ''}`}
      data-test-id={testId}
      tabIndex={0}
      onMouseDown={onMouseDown}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <div className="UrlPatternTextDropdown__menuRowContent">
        <div className="UrlPatternTextDropdown__menuRowLeading">
          {rowIcon != null && (
            <Icon icon={rowIcon} version="v2" size="small" testId={`${testId}-row-icon`} />
          )}
          <div
            className={['UrlPatternTextDropdown__suggestionTitle', titleClassName].filter(Boolean).join(' ')}
          >
            <div className="UrlPatternTextDropdown__suggestionTitleWithHelp">
              <Truncate
                testId={`${testId}-primaryText`}
                truncateFrom="end"
                isResponsive
                tooltip={{ position: 'bottom' }}
              >
                {title}
              </Truncate>
              {titleHelpText ? (
                  <Help
                    type="basic"
                    alignment="right"
                    text={titleHelpText}
                    testId={`${testId}-title-help-icon`}
                  />
              ) : null}
            </div>
          </div>
        </div>
        {hasSubtitle || showChevron ? secondary : null}
      </div>
    </div>
  )
}

export default UrlPatternMenuRow
